import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';

/**
 * Single source of truth for workflow output files.
 *
 * Storage keys ALWAYS start with the owner's user id — the `user_files` bucket
 * policy requires it — and the exact same key is stored in
 * `file_storage.file_path`, so the download path can never drift from the
 * upload path.
 */

/** Collapse accidental duplicate slashes so DB paths match real storage keys. */
export const normalizeStorageKey = (key: string) =>
  key.replace(/\/{2,}/g, '/').replace(/^\//, '');

export const buildStorageKey = (userId: string, runId: string, fileName: string) =>
  normalizeStorageKey(`${userId}/workflow-outputs/${runId}/${fileName}`);

export const recordsToCsv = (records: Record<string, unknown>[]): string => {
  if (!records.length) return '';
  const headers = Object.keys(records[0]).filter(
    k => !k.startsWith('_') || k === '_lead_score' || k === '_confidence',
  );
  const humanHeaders = headers.map(h =>
    h.replace(/^_/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
  );

  let csv = humanHeaders.join(',') + '\n';
  records.forEach(record => {
    csv += headers
      .map(h => {
        const val = record[h];
        const str =
          val === null || val === undefined
            ? ''
            : typeof val === 'object'
              ? JSON.stringify(val)
              : String(val);
        return `"${str.replace(/"/g, '""')}"`;
      })
      .join(',') + '\n';
  });
  return csv;
};

/** Make sure the "/Workflow Outputs/vN/" folder rows exist for the file browser. */
const ensureFolders = async (userId: string, runVersion: number) => {
  const folders = [
    { name: 'Workflow Outputs', path: '/Workflow Outputs/', parent: '/' },
    { name: `v${runVersion}`, path: `/Workflow Outputs/v${runVersion}/`, parent: '/Workflow Outputs/' },
  ];

  for (const folder of folders) {
    const { data: existing } = await supabase
      .from('file_storage')
      .select('id')
      .eq('user_id', userId)
      .eq('folder_path', folder.parent)
      .eq('file_name', folder.name)
      .eq('is_folder', true)
      .maybeSingle();

    if (!existing) {
      await supabase.from('file_storage').insert({
        user_id: userId,
        file_name: folder.name,
        file_path: folder.path,
        folder_path: folder.parent,
        is_folder: true,
      });
    }
  }
};

export interface SaveOutputArgs {
  userId: string;
  runId: string;
  runVersion: number;
  /** Which slice of the run to export. */
  dataType: 'raw' | 'cleaned' | 'predicted';
  /** Human label used in the file name, e.g. "Cleaned Dataset". */
  label: string;
  /** Optional pre-fetched rows; otherwise they are read from workflow_data. */
  records?: Record<string, unknown>[];
}

export interface SaveOutputResult {
  saved: boolean;
  reason?: string;
  fileName?: string;
  storagePath?: string;
}

/**
 * Generates a CSV for a workflow run, uploads it, and records it in file_storage.
 * Returns `saved: false` (never throws for "no data") when the run has no rows.
 */
export const saveWorkflowOutputFile = async ({
  userId,
  runId,
  runVersion,
  dataType,
  label,
  records,
}: SaveOutputArgs): Promise<SaveOutputResult> => {
  let rows = records;

  if (!rows) {
    const { data, error } = await supabase
      .from('workflow_data')
      .select('row_data')
      .eq('workflow_run_id', runId)
      .eq('data_type', dataType)
      .order('row_index');
    if (error) throw error;
    rows = (data || []).map(r => r.row_data as Record<string, unknown>);
  }

  if (!rows.length) return { saved: false, reason: `No ${dataType} data available` };

  const csv = recordsToCsv(rows);
  const fileName = `${label} - v${runVersion} - ${format(new Date(), 'yyyy-MM-dd')}.csv`;
  const storagePath = buildStorageKey(userId, runId, fileName);
  const blob = new Blob([csv], { type: 'text/csv' });

  const { error: uploadError } = await supabase.storage
    .from('user_files')
    .upload(storagePath, blob, { upsert: true, contentType: 'text/csv' });
  if (uploadError) throw uploadError;

  await ensureFolders(userId, runVersion);

  const folderPath = `/Workflow Outputs/v${runVersion}/`;

  // Replace any earlier row for the same key so the list never shows duplicates.
  await supabase.from('file_storage').delete().eq('user_id', userId).eq('file_path', storagePath);

  const { error: dbError } = await supabase.from('file_storage').insert({
    user_id: userId,
    file_name: fileName,
    file_path: storagePath,
    folder_path: folderPath,
    mime_type: 'text/csv',
    size: blob.size,
    is_folder: false,
  });
  if (dbError) throw dbError;

  return { saved: true, fileName, storagePath };
};

/**
 * Downloads a stored object, verifying it really exists first so the user gets
 * a meaningful message instead of a raw "Object not found".
 */
export const downloadStoredFile = async (filePath: string, fileName: string) => {
  const key = normalizeStorageKey(filePath);
  const lastSlash = key.lastIndexOf('/');
  const dir = lastSlash === -1 ? '' : key.slice(0, lastSlash);
  const base = key.slice(lastSlash + 1);

  const { data: listing, error: listError } = await supabase.storage
    .from('user_files')
    .list(dir, { search: base, limit: 100 });

  if (listError) throw new Error(listError.message);
  if (!listing?.some(o => o.name === base)) {
    throw new Error('This file is no longer stored. Generate it again to download a fresh copy.');
  }

  const { data, error } = await supabase.storage.from('user_files').download(key);
  if (error) throw new Error(error.message);

  const url = URL.createObjectURL(data);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
};
